/* Henne · Customers' Motorcycles gallery (snippets/henne-customer-bikes.liquid).
 * Thumbnails are <button>s; the lightbox is a native <dialog> (Esc closes it natively).
 * Prev/next buttons, Left/Right/Home/End keys, swipe on touch screens, click outside the photo to close,
 * focus goes back to the thumbnail of the last photo viewed. No libraries. */
(() => {
  if (customElements.get("henne-customer-bikes")) return;

  const FALLBACK_SIZES = "(min-width: 1640px) 1600px, 100vw";
  // Pinch-zoomed in: one-finger drags pan the photo, they are not swipes.
  const zoomed = () => !!(window.visualViewport && window.visualViewport.scale > 1.01);

  // The theme's tab headers and mobile accordion buttons are click-only <div>s. This gallery becomes the default tab
  // and pushes FITMENT / SHIPPING behind them, so on pages where it renders make them keyboard-operable
  // (tabs: roving tabindex, Left/Right/Home/End, Enter/Space; accordion buttons: Enter/Space, aria-expanded).
  // The theme's own click handlers do the switching; the server HTML of the section stays as it was.
  function enhanceTabs(section) {
    if (!section || section.hcbKeyboard) return;
    section.hcbKeyboard = true;

    const list = section.querySelector(".m-tabs__header");
    const headers = list ? Array.from(list.querySelectorAll(".m-tab-header")) : [];
    if (headers.length) {
      list.setAttribute("role", "tablist");
      headers.forEach((h) => h.setAttribute("role", "tab"));
      const sync = () => {
        const any = headers.some((h) => h.classList.contains("active"));
        headers.forEach((h, i) => {
          const on = h.classList.contains("active");
          h.setAttribute("aria-selected", on ? "true" : "false");
          h.tabIndex = on || (!any && i === 0) ? 0 : -1;
        });
      };
      sync();
      new MutationObserver(sync).observe(list, { subtree: true, attributes: true, attributeFilter: ["class"] });
      list.addEventListener("keydown", (e) => {
        const i = headers.indexOf(e.target);
        if (i < 0) return;
        const n = headers.length;
        let j = -1;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.target.click(); return; }
        if (e.key === "ArrowRight") j = (i + 1) % n;
        else if (e.key === "ArrowLeft") j = (i - 1 + n) % n;
        else if (e.key === "Home") j = 0;
        else if (e.key === "End") j = n - 1;
        if (j < 0) return;
        e.preventDefault();
        headers[j].focus();
        headers[j].click();
      });
    }

    const items = Array.from(section.querySelectorAll(".m-accordion--item"));
    const buttons = items.map((it) => it.querySelector(".m-accordion--item-button"));
    if (buttons.some(Boolean)) {
      buttons.forEach((b) => { if (b) { b.setAttribute("role", "button"); b.tabIndex = 0; } });
      const syncAcc = () => items.forEach((it, i) => {
        if (buttons[i]) buttons[i].setAttribute("aria-expanded", it.classList.contains("open") ? "true" : "false");
      });
      syncAcc();
      items.forEach((it) => new MutationObserver(syncAcc).observe(it, { attributes: true, attributeFilter: ["class"] }));
      section.addEventListener("keydown", (e) => {
        if ((e.key === "Enter" || e.key === " ") && buttons.includes(e.target)) { e.preventDefault(); e.target.click(); }
      });
    }
  }

  class HenneCustomerBikes extends HTMLElement {
    connectedCallback() {
      if (this.hcbReady) return;
      this.hcbReady = true;
      this.thumbs = Array.from(this.querySelectorAll("[data-hcb-index]"));
      this.dialog = this.querySelector(".hcb-lightbox");
      if (!this.thumbs.length || !this.dialog) return;
      enhanceTabs(this.closest("m-product-details-tabs"));

      this.img = this.dialog.querySelector("[data-hcb-img]");
      this.caption = this.dialog.querySelector("[data-hcb-caption]");
      this.counter = this.dialog.querySelector("[data-hcb-counter]");
      this.live = this.dialog.querySelector("[data-hcb-live]");
      this.stage = this.dialog.querySelector("[data-hcb-stage]");
      this.figure = this.dialog.querySelector(".hcb-lightbox__figure");
      this.showCaptions = this.dataset.captions !== "false";
      this.index = 0;

      this.thumbs.forEach((btn, i) => btn.addEventListener("click", () => this.open(i)));
      this.dialog.querySelector("[data-hcb-close]").addEventListener("click", () => this.dialog.close());
      const prev = this.dialog.querySelector("[data-hcb-prev]");
      const next = this.dialog.querySelector("[data-hcb-next]");
      prev && prev.addEventListener("click", () => this.show(this.index - 1));
      next && next.addEventListener("click", () => this.show(this.index + 1));

      // A click on the dark area around the photo closes the lightbox.
      this.dialog.addEventListener("click", (e) => {
        if (e.target === this.dialog || e.target === this.stage || e.target === this.figure) this.dialog.close();
      });
      this.dialog.addEventListener("keydown", (e) => {
        const n = this.thumbs.length;
        if (n < 2) return;
        if (e.key === "ArrowLeft") { e.preventDefault(); this.show(this.index - 1); }
        else if (e.key === "ArrowRight") { e.preventDefault(); this.show(this.index + 1); }
        else if (e.key === "Home") { e.preventDefault(); this.show(0); }
        else if (e.key === "End") { e.preventDefault(); this.show(n - 1); }
      });
      this.dialog.addEventListener("close", () => {
        document.documentElement.classList.remove("hcb-locked");
        clearTimeout(this.liveTimer);
        if (this.live) this.live.textContent = "";
        const t = this.thumbs[this.index];
        t && t.focus({ preventScroll: false });
      });

      // Swipe left / right on touch screens (not while pinch-zoomed in, then a drag pans the photo).
      let x0 = null, y0 = null;
      this.stage.addEventListener("touchstart", (e) => {
        if (e.touches.length !== 1 || zoomed()) { x0 = null; return; }
        x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
      }, { passive: true });
      this.stage.addEventListener("touchend", (e) => {
        if (x0 === null || this.thumbs.length < 2 || zoomed()) { x0 = null; return; }
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.show(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      this.img.addEventListener("load", () => this.img.classList.remove("is-loading"));
      this.img.addEventListener("error", () => this.img.classList.remove("is-loading"));
      window.addEventListener("resize", () => { if (this.dialog.open) this.fit(); }, { passive: true });
    }

    open(i) {
      if (typeof this.dialog.showModal !== "function") {
        // Very old browsers without <dialog>: open the large photo in a new tab.
        const d = this.thumbs[i] && this.thumbs[i].dataset;
        d && d.hcbSrc && window.open(d.hcbSrc, "_blank", "noopener");
        return;
      }
      // Open first (same task, so nothing paints in between) so the photo box can be measured in show().
      if (!this.dialog.open) {
        document.documentElement.classList.add("hcb-locked");
        this.dialog.showModal();
      }
      this.show(i);
    }

    // The photo's box: its pixel size fitted into the free space (open dialog only). null when the size is unknown.
    box(d) {
      const w = +d.hcbW, h = +d.hcbH;
      const availW = this.figure.clientWidth;
      const availH = parseFloat(getComputedStyle(this.img).maxHeight) || window.innerHeight;
      if (!(w > 0 && h > 0) || !availW) return null;
      const s = Math.min(1, availW / w, availH / h);
      return [Math.floor(w * s), Math.floor(h * s)];
    }

    // Size the photo box before the photo loads, so nothing jumps, and tell the browser the real display width
    // (sizes) so it downloads the matching srcset file instead of a full-viewport one.
    fit() {
      const b = this.box(this.thumbs[this.index].dataset), img = this.img;
      if (!b) { img.style.width = ""; img.style.height = ""; img.sizes = FALLBACK_SIZES; return; }
      img.style.width = `${b[0]}px`;
      img.style.height = `${b[1]}px`;
      img.sizes = `${Math.max(1, b[0])}px`;
    }

    announce(text) {
      if (!this.live) return;
      clearTimeout(this.liveTimer);
      this.live.textContent = "";
      // Set after a beat so the change is announced (also right after the dialog opens and for repeated text).
      this.liveTimer = setTimeout(() => { this.live.textContent = text; }, 80);
    }

    show(i) {
      const n = this.thumbs.length;
      this.index = ((i % n) + n) % n;
      const d = this.thumbs[this.index].dataset;
      const img = this.img;
      img.classList.add("is-loading");
      if (+d.hcbW > 0 && +d.hcbH > 0) {
        img.setAttribute("width", d.hcbW);
        img.setAttribute("height", d.hcbH);
      } else {
        img.removeAttribute("width");
        img.removeAttribute("height");
      }
      this.fit();
      img.srcset = d.hcbSrcset || "";
      img.src = d.hcbSrc;
      if (img.complete && img.naturalWidth && img.currentSrc) img.classList.remove("is-loading");

      this.caption.textContent = this.showCaptions ? d.hcbCaption || "" : "";
      this.counter.textContent = n > 1 ? `${this.index + 1} / ${n}` : "";
      const pos = `photo ${this.index + 1} of ${n}`;
      this.announce(d.hcbCaption ? (n > 1 ? `${d.hcbCaption}, ${pos}` : d.hcbCaption) : d.hcbAlt || (n > 1 ? `Photo ${this.index + 1} of ${n}` : ""));

      // Warm up the neighbours (at the size they will be shown) so prev / next feel instant.
      if (n > 1) [this.index + 1, this.index - 1].forEach((j) => {
        const nd = this.thumbs[((j % n) + n) % n].dataset;
        const nb = this.box(nd);
        const pre = new Image();
        pre.sizes = nb ? `${Math.max(1, nb[0])}px` : FALLBACK_SIZES;
        if (nd.hcbSrcset) pre.srcset = nd.hcbSrcset;
        pre.src = nd.hcbSrc;
      });
    }
  }

  customElements.define("henne-customer-bikes", HenneCustomerBikes);
})();
